import { isNumber } from "@sperrdatei/dto/utils";
import { useContext, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";
import { BlockEventList } from "../../components/block-event-list";
import { Modal } from "../../components/modal";
import { BlockCheckResult } from "../check-number/block-result";
import { CheckNumberForm } from "../check-number/form";
import { FullNumberCheckResult } from "../check-number/full-number-result";

import type {
  FreeNumberSuccessResponse,
  NumberCheckResponseSuccessResponse,
} from "@sperrdatei/dto/types";

export type FreeNumberPageProps = object;

export const FreeNumberPage: React.FC<FreeNumberPageProps> = () => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [searchedNumber, setSearchedNumber] = useState("");
  const [foundNumber, setFoundNumber] = useState<
    NumberCheckResponseSuccessResponse | null | undefined
  >(undefined);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [result, setResult] = useState<FreeNumberSuccessResponse | undefined>(
    undefined,
  );

  const showForm = !result && !foundNumber;

  const showFullNumberCheckResult =
    !result && foundNumber && isNumber(searchedNumber);
  const showBlockCheckResult =
    !result && foundNumber && !isNumber(searchedNumber);
  const showHistory =
    !result && foundNumber?.history && foundNumber?.history.length > 0;
  const showActionButtons = !result && foundNumber;

  const FreeBlockEventModal: React.FC = () => {
    return (
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          isNumber(searchedNumber) ? "Nummer freigeben" : "Block freigeben"
        }
        spreadButtons={true}
        primaryAction={{
          label: "Freigeben",
          buttonStyle: "danger",
          showLoadingIndicator: isLoading,
          onClick: async () => {
            setIsLoading(true);
            const response = await apiFetch(`/api/number/${searchedNumber}`, {
              method: "DELETE",
            });
            setIsLoading(false);

            if (response.ok) {
              setResult((await response.json()) as FreeNumberSuccessResponse);
              setIsModalOpen(false);
              return;
            }

            navigate("/error");
            return;
          },
        }}
        secondaryAction={{
          label: "Abbrechen",
          buttonStyle: "secondary",
          onClick: async () => {
            setIsModalOpen(false);
            return;
          },
        }}
      >
        <p>
          Sind Sie sicher, dass Sie
          {isNumber(searchedNumber) ? " die Nummer " : " den Block "}
          <strong style={{ whiteSpace: "nowrap" }}>
            {searchedNumber}
          </strong>{" "}
          freigeben wollen?
        </p>
      </Modal>
    );
  };

  return (
    <div className="container ms-0">
      <FreeBlockEventModal />

      <h3 className="page-caption mb-3">Nummer freigeben</h3>

      {result ? (
        <>
          <h6>Vorgang abgeschlossen</h6>
          <p>
            <strong>Ergebnis</strong>: <br />
            <span>
              Insgesamt {result.deleted > 1 ? "wurden" : "wurde"}{" "}
              <strong>{result.deleted}</strong>
              {result.deleted > 1 ? " Nummern" : " Nummer"} freigegeben.
              <br />
            </span>
          </p>
          <button
            className="btn btn-primary"
            onClick={() => {
              setResult(undefined);
              setFoundNumber(undefined);
              setSearchedNumber("");
            }}
          >
            OK
          </button>
        </>
      ) : null}

      {showForm ? (
        <>
          <p>
            Hier können Sie eine Nummer oder einen Rufnummernblock wieder
            freigeben.
          </p>
          <CheckNumberForm
            setFoundNumber={(number) => setFoundNumber(number)}
            setSearchedNumber={(number) => setSearchedNumber(number)}
          />
        </>
      ) : null}

      {showFullNumberCheckResult ? (
        <FullNumberCheckResult
          number={foundNumber.number}
          searchedNumber={searchedNumber}
        />
      ) : showBlockCheckResult ? (
        <BlockCheckResult
          numbers={foundNumber.numbers}
          searchedNumber={searchedNumber}
        />
      ) : null}

      {showHistory ? (
        <>
          {" "}
          <h4 className="mt-4">Historie</h4>
          <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
            <BlockEventList events={foundNumber?.history ?? []} />
          </div>
        </>
      ) : null}

      <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0 d-flex justify-content-between">
        {showActionButtons ? (
          <>
            <button
              type="button"
              className="btn btn-primary mb-3"
              onClick={() => {
                setFoundNumber(undefined);
                setSearchedNumber("");
              }}
            >
              Zurück
            </button>

            {foundNumber.number || foundNumber.numbers ? (
              <button
                type="button"
                className="btn btn-danger mb-3"
                onClick={() => setIsModalOpen(true)}
              >
                Freigeben
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
};
